import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { Col } from '../../base/Grid';
import Theme from '../../theme';

import {
  Section,
  HeroTextContainer,
  BackgroundContainer,
  HeroTextCol,
  HeroTextRow,
  HeroProductH1,
  HeroHeadline,
  HeroSubheadline,
  CtaButton,
} from './styles';

// constants
const LISTENER_THROTTLE = 100;

const isWindowBelowBreakpoint = (breakpoint) => {
  if (typeof window !== 'undefined' && window.innerWidth >= breakpoint) {
    return false;
  }
  return true;
};

const geImageForBreakpoint = (
  mobileBreakpoint,
  tabletBreakpoint,
  mobile,
  tablet,
  desktop,
) => {
  const isMobile = isWindowBelowBreakpoint(mobileBreakpoint);
  const isTablet = isWindowBelowBreakpoint(tabletBreakpoint);
  if (isMobile) {
    return mobile;
  }
  if (isTablet) {
    return tablet || desktop; // Tablet is not required, sometimes desktop is used as the tablet image
  }
  return desktop;
};

export const HeroSectionContainer = ({
  id,
  backgroundImage,
  // backgroundImageLowRes,
  backgroundImageTablet,
  // backgroundImageTabletLowRes,
  backgroundImageMobile,
  // backgroundImageMobileLowRes,
  product,
  headline,
  subHeadline,
  ctaPrimary,
  ctaSecondary,
  mobileBreakpoint,
  tabletBreakpoint,
}) => {
  const ctaButton1 = useRef();
  const ctaButton2 = useRef();

  const checkCtaPositions = () => {
    const { offsetTop: offsetTopCheck1 } = ctaButton1.current || {};
    const { offsetTop: offsetTopCheck2 } = ctaButton2.current || {};
    return offsetTopCheck1 !== offsetTopCheck2;
  };

  // const [isImageLoaded, setImageLoaded] = useState(false);
  // const [lowResImage, setLowResImage] = useState(
  //   geImageForBreakpoint(mobileBreakpoint, tabletBreakpoint, backgroundImageMobileLowRes, backgroundImageTabletLowRes, backgroundImageLowRes),
  // );
  const [highResImage, setHighResImage] = useState(
    geImageForBreakpoint(
      mobileBreakpoint,
      tabletBreakpoint,
      backgroundImageMobile,
      backgroundImageTablet,
      backgroundImage,
    ),
  );
  const [ctaButtonsStacked, setCtaButtonsStacked] = useState(
    checkCtaPositions(),
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // browser-only code - react-static has some server side rendering for build process
      window.addEventListener(
        // TODO: add removeEventListener
        'resize',
        debounce(() => {
          // setLowResImage(geImageForBreakpoint(mobileBreakpoint, tabletBreakpoint, backgroundImageMobileLowRes,
          //   backgroundImageTabletLowRes, backgroundImageLowRes));
          setHighResImage(
            geImageForBreakpoint(
              mobileBreakpoint,
              tabletBreakpoint,
              backgroundImageMobile,
              backgroundImageTablet,
              backgroundImage,
            ),
          );
          setCtaButtonsStacked(checkCtaPositions());
        }, LISTENER_THROTTLE),
      );
    }
  }, []);

  // useEffect(() => {
  //   const img = new Image();
  //   setImageLoaded(false);

  //   img.onload = () => setImageLoaded(true);

  //   img.src = highResImage;
  // }, [highResImage]);

  return (
    <Section
      id={`section-${id}`}
      role="banner"
      data-module={`${`section-${id}`}:hero-module:A`}>
      <BackgroundContainer
        // dataLoading={(!isImageLoaded).toString()}
        highResImage={highResImage}
        // lowResImage={lowResImage}
        id={`background-image-${id}`}
      />
      <HeroTextContainer>
        <HeroTextRow>
          <HeroTextCol xsmall={12} medium={6}>
            <div>
              <HeroProductH1
                variant="p"
                component="h1"
                weight={400}
                id={`hero-product-name-${id}`}>
                {product}
              </HeroProductH1>
              <HeroHeadline
                component="p"
                variant="h1"
                align="left"
                id={`hero-headline-${id}`}>
                {headline}
              </HeroHeadline>
              <HeroSubheadline
                component="p"
                align="left"
                id={`hero-subheadline-${id}`}>
                {subHeadline}
              </HeroSubheadline>
              {ctaPrimary && (
                <CtaButton
                  variant="secondary"
                  role="link"
                  aria-label={ctaPrimary.ariaLabel}
                  id={`cta-primary-${id}`}
                  onClick={() => {
                    window.location = ctaPrimary.link;
                  }}
                  ref={ctaButton1}>
                  {ctaPrimary.buttonText}
                </CtaButton>
              )}
              {ctaSecondary && (
                <CtaButton
                  variant="tertiary"
                  role="link"
                  aria-label={ctaSecondary.ariaLabel}
                  id={`cta-secondary-${id}`}
                  applyTopPadding={ctaButtonsStacked}
                  onClick={() => {
                    window.location = ctaSecondary.link;
                  }}
                  ref={ctaButton2}>
                  {ctaSecondary.buttonText}
                </CtaButton>
              )}
            </div>
          </HeroTextCol>
          <Col xsmall={0} medium={6} />
        </HeroTextRow>
      </HeroTextContainer>
    </Section>
  );
};

HeroSectionContainer.propTypes = {
  id: PropTypes.string.isRequired,
  /** base64 encoded desktop "fuzzy" image for initial display before full image renders */
  backgroundImageLowRes: PropTypes.string.isRequired,
  /** href or src of desktop image to load */
  backgroundImage: PropTypes.string.isRequired,
  /** base64 encoded mobile "fuzzy" image for initial display before full image renders */
  backgroundImageTabletLowRes: PropTypes.string,
  /** href or src of mobile image to load */
  backgroundImageTablet: PropTypes.string,
  /** base64 encoded mobile "fuzzy" image for initial display before full image renders */
  backgroundImageMobileLowRes: PropTypes.string.isRequired,
  /** href or src of mobile image to load */
  backgroundImageMobile: PropTypes.string.isRequired,
  product: PropTypes.string.isRequired,
  headline: PropTypes.string.isRequired,
  subHeadline: PropTypes.string.isRequired,
  /** Props passed to primary CTA button - if object not provided, will now show button */
  ctaPrimary: PropTypes.shape({
    link: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    ariaLabel: PropTypes.string,
  }),
  /** Props passed to secondary CTA button - if object not provided, will now show button */
  ctaSecondary: PropTypes.shape({
    link: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    ariaLabel: PropTypes.string,
  }),
  /** Mobile breakpoint */
  mobileBreakpoint: PropTypes.number,
  /** Tablet breakpoint */
  tabletBreakpoint: PropTypes.number,
};

HeroSectionContainer.defaultProps = {
  mobileBreakpoint: Theme.breakpoints.values.medium,
  tabletBreakpoint: Theme.breakpoints.values.large,
};

export default HeroSectionContainer;
